import { useState } from "react";

import { CheckIcon, Segments } from "@signet/ui";
import { NowPanelHead, useSeenQuery } from "@signet/web-kit";
import { fetchTodos, todoKeys } from "./api.ts";
import { TodoDrawer } from "./drawer.tsx";
import { QuickAdd, TodoRow, useMoveTodo, useOpenTodo, useTodoChoices } from "./row.tsx";
import { useSortable } from "./sortable.ts";
import { StatusMenu } from "./telegram.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   To-do on Now, laid out the way Ben asked (2026-10-08): every open to-do,
   one line each, scrolling inside the panel so the whole list can be worked
   from Now without opening the To-do page. New ones go on top; a ticked one
   swipes away and is gone, and the To-do page's Done tab keeps it (Ben, 2026-10-27).

   A switch between All and each person (Jay, 2026-10-08). It decides who a
   new to-do goes to as well: under All, whoever adds it; under a name, that
   person. Remembered per browser, so Ben's Now opens on Ben.
   ────────────────────────────────────────────────────────────────────────── */

const WHO_KEY = "signet.todo.now.who";

function useWho(): [string, (next: string) => void] {
  const [who, setWho] = useState(() => {
    try {
      return localStorage.getItem(WHO_KEY) ?? "all";
    } catch {
      return "all";
    }
  });
  const pick = (next: string) => {
    setWho(next);
    try {
      localStorage.setItem(WHO_KEY, next);
    } catch {
      // Private window or blocked storage: the switch still works, it just isn't remembered.
    }
  };
  return [who, pick];
}

export function TodoNowPanel() {
  const list = useSeenQuery({ queryKey: todoKeys.list("now"), queryFn: () => fetchTodos("now"), refetchInterval: 60_000 });
  const { people } = useTodoChoices();
  const [picked, setWho] = useWho();
  const [open, setOpen] = useState<string | null>(null);
  // A remembered person who has since lost access falls back to All.
  const person = people.find((p) => p.id === picked);
  const all = list.data?.items ?? [];
  const items = person ? all.filter((t) => t.assignee?.id === person.id) : all;
  const openCount = items.filter((t) => !t.done).length;
  const opened = useOpenTodo(all, open);
  const move = useMoveTodo(todoKeys.list("now"));
  const sort = useSortable({ ids: items.map((t) => t.id), groupOf: (id) => (items.find((t) => t.id === id)?.pinned ? "pinned" : "rest"), onMove: move });

  return (
    <section className="panel td-now">
      <NowPanelHead
        label="Team"
        title="To-do"
        sub={list.data ? `${openCount} open${person ? ` for ${person.name}` : ""}` : "\u00a0"}
        side={
          <span className="td-head-actions">
            <StatusMenu />
            {people.length ? (
              <Segments label="Whose to-dos" value={person?.id ?? "all"} options={[{ value: "all", label: "All" }, ...people.map((p) => ({ value: p.id, label: p.name }))]} onChange={setWho} />
            ) : null}
          </span>
        }
      />
      <div className="panel-body">
        <QuickAdd assignee={person?.id} />
        {list.isError ? <p className="faint">Couldn't load the to-dos.</p> : null}
        {list.data && !openCount ? (
          <p className="td-empty">
            <CheckIcon size={15} /> {person ? `Nothing for ${person.name}.` : "Nothing to do."}
          </p>
        ) : null}
        {items.length ? (
          <div className="td-scroll" tabIndex={0} aria-label="To-dos">
            <ul className="td-list">{items.map((t) => <TodoRow key={t.id} todo={t} sort={sort} showNotes={false} onOpen={() => setOpen(t.id)} />)}</ul>
          </div>
        ) : null}
      </div>
      <TodoDrawer todo={opened} onClose={() => setOpen(null)} />
    </section>
  );
}
